import { useState } from 'react'
import { useDocuments, useDeleteDocument, useUpdateDocument, getDocumentDownloadUrl } from '../api/documents'
import { useClients } from '../api/clients'
import { DocumentUploadModal } from '../components/documents/DocumentUploadModal'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { cn, formatDate } from '../lib/utils'
import type { Document } from '@1130-studio/shared'

const typeLabels: Record<string, string> = {
  contract: 'Договор',
  invoice: 'Счёт',
  act: 'Акт',
  specification: 'ТЗ',
  other: 'Другое',
}

const statusLabels: Record<string, string> = {
  draft: 'Черновик',
  review: 'На проверке',
  sent: 'Отправлен',
  signed: 'Подписан',
  archived: 'В архиве',
}

const statusColors: Record<string, string> = {
  draft: 'bg-text-tertiary',
  review: 'bg-accent-orange',
  sent: 'bg-accent-blue',
  signed: 'bg-accent-lime',
  archived: 'bg-text-tertiary',
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileIcon(mimeType: string): React.ReactNode {
  if (mimeType.startsWith('image/')) {
    return (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    )
  }
  if (mimeType === 'application/pdf') {
    return (
      <svg className="w-6 h-6 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    )
  }
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  )
}

export function DocumentsPage() {
  const [showUpload, setShowUpload] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null)
  const [filterType, setFilterType] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterClient, setFilterClient] = useState('')

  const { data: documents, isLoading } = useDocuments({
    type: filterType || undefined,
    status: filterStatus || undefined,
    clientId: filterClient || undefined,
  })
  const { data: clientsData } = useClients()
  const deleteDocument = useDeleteDocument()
  const updateDocument = useUpdateDocument()

  const handleDelete = async (doc: Document) => {
    if (confirm(`Удалить документ "${doc.name}"?`)) {
      await deleteDocument.mutateAsync(doc.id)
    }
  }

  const handleStatusChange = async (doc: Document, newStatus: string) => {
    await updateDocument.mutateAsync({
      id: doc.id,
      data: { status: newStatus },
    })
    setSelectedDoc(null)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-secondary">Загрузка...</div>
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Документы</h1>
          <p className="text-text-secondary mt-1">
            {documents?.length || 0} документов
          </p>
        </div>

        <Button variant="primary" onClick={() => setShowUpload(true)}>
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          Загрузить
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 bg-bg-tertiary border border-border-medium rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent-lime/50"
        >
          <option value="">Все типы</option>
          {Object.entries(typeLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 bg-bg-tertiary border border-border-medium rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent-lime/50"
        >
          <option value="">Все статусы</option>
          {Object.entries(statusLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <select
          value={filterClient}
          onChange={(e) => setFilterClient(e.target.value)}
          className="px-3 py-2 bg-bg-tertiary border border-border-medium rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent-lime/50"
        >
          <option value="">Все клиенты</option>
          {clientsData?.items?.map((client: any) => (
            <option key={client.id} value={client.id}>
              {client.companyName || client.contactName}
            </option>
          ))}
        </select>

        {(filterType || filterStatus || filterClient) && (
          <button
            onClick={() => { setFilterType(''); setFilterStatus(''); setFilterClient('') }}
            className="px-3 py-2 text-sm text-text-tertiary hover:text-white transition-colors"
          >
            Сбросить
          </button>
        )}
      </div>

      {/* Documents Grid */}
      {documents && documents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => (
            <Card key={doc.id} className="group hover:border-border-medium transition-colors">
              <div className="p-4">
                {/* Header */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-bg-tertiary flex items-center justify-center text-text-tertiary">
                    {getFileIcon(doc.mimeType)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{doc.name}</h3>
                    <p className="text-xs text-text-tertiary">
                      {formatFileSize(doc.size)} • {formatDate(doc.createdAt)}
                    </p>
                  </div>
                </div>

                {/* Badges */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs px-2 py-0.5 bg-bg-tertiary rounded-full text-text-secondary">
                    {typeLabels[doc.type]}
                  </span>
                  <span className={cn(
                    'text-xs px-2 py-0.5 rounded-full text-bg-primary font-medium',
                    statusColors[doc.status]
                  )}>
                    {statusLabels[doc.status]}
                  </span>
                </div>

                {/* Project */}
                {doc.project && (
                  <p className="text-xs text-text-tertiary mb-3">
                    Проект: {doc.project.name}
                  </p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t border-border-subtle">
                  <a
                    href={getDocumentDownloadUrl(doc.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button variant="secondary" className="w-full text-sm">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Скачать
                    </Button>
                  </a>

                  <button
                    onClick={() => setSelectedDoc(doc)}
                    className="p-2 text-text-tertiary hover:text-text-primary hover:bg-bg-tertiary rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="12" cy="5" r="1" />
                      <circle cx="12" cy="19" r="1" />
                    </svg>
                  </button>

                  <button
                    onClick={() => handleDelete(doc)}
                    className="p-2 text-text-tertiary hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-text-tertiary">
          <svg
            className="w-16 h-16 mx-auto mb-4 opacity-30"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <p>Нет документов</p>
          <p className="text-sm mt-1">Загрузите первый документ</p>
        </div>
      )}

      {/* Upload Modal */}
      <DocumentUploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
      />

      {/* Status Change Modal */}
      {selectedDoc && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100]">
          <Card className="w-full max-w-sm">
            <div className="p-6">
              <h2 className="text-lg font-bold mb-4">Изменить статус</h2>
              <p className="text-sm text-text-secondary mb-4">{selectedDoc.name}</p>

              <div className="space-y-2">
                {Object.entries(statusLabels).map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => handleStatusChange(selectedDoc, value)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                      selectedDoc.status === value
                        ? 'bg-accent-lime/15 text-accent-lime'
                        : 'hover:bg-bg-tertiary text-text-secondary'
                    )}
                  >
                    <span className={cn('w-2 h-2 rounded-full', statusColors[value])} />
                    {label}
                  </button>
                ))}
              </div>

              <Button
                variant="secondary"
                className="w-full mt-4"
                onClick={() => setSelectedDoc(null)}
              >
                Закрыть
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
